/**
 * Single source of truth for the "pipeline surface active" condition
 * (SE-5509).
 *
 * The pipeline surface (SE-6071) is active when BOTH of the following hold:
 *
 * 1. `isPipelineSurfaceOn()` resolves true — the build-time `PIPELINE_SURFACE`
 *    const, which defaults "off" so production keeps the legacy
 *    watchlist/roster behavior, unless a non-production build carries the
 *    SE-6257 runtime override, and
 * 2. The current user clears the Manager+ gate (`useIsPipelineManager` —
 *    Owner/Admin/Manager), which fails closed while `/auth/me` is in flight.
 *
 * This mirrors the exact precedence `resolveKanbanSourceType` uses in the
 * player-kanban MainContainer (`isPipelineEnabled && isManagerPlus`), so the
 * nav promotion (top-level Pipeline entry) and the Scouting Boards-tab hide
 * flip in lockstep with the surface itself. When the flag is off — the
 * production default — this is always `false`, so nav and the Scouting tabs
 * are unchanged and CI e2e (which builds with the flag off) is unaffected.
 * @module features/player-kanban/hooks/useIsPipelineSurfaceActive
 */

import { isPipelineSurfaceOn } from "@/config/pipelineSurface";

import { useIsPipelineManager } from "./useIsPipelineManager";

/**
 * Whether the pipeline surface is active for the current user.
 * @returns `true` only when `isPipelineSurfaceOn()` AND the user is
 * Manager+; `false` in every other case (flag off, sub-Manager, or the
 * fail-closed auth-loading state).
 */
export function useIsPipelineSurfaceActive(): boolean {
  const isManagerPlus = useIsPipelineManager();
  return isPipelineSurfaceOn() && isManagerPlus;
}
